import { useState, useEffect } from "react";
import api from "@/utils/api";
import { TrendingUp, TrendingDown } from "lucide-react";

const LiveTicker = () => {
  const [tickers, setTickers] = useState([]);
  const [loading, setLoading] = useState(true);

  const symbols = ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'TSLA', 'NVDA', 'META', 'NFLX'];

  useEffect(() => {
    fetchPrices();
    const interval = setInterval(fetchPrices, 10000); // Update every 10s
    return () => clearInterval(interval);
  }, []);

  const fetchPrices = async () => {
    try {
      const promises = symbols.map(symbol =>
        api.get(`/live-price/${symbol}`).catch(() => null)
      );

      const results = await Promise.all(promises);
      // Only keep tickers whose price AND change_percent are real numbers —
      // a partial payload (e.g. backend mid-restart or a market-data gap) used
      // to crash the whole app at `.toFixed()` since LiveTicker renders in every
      // page layout. Filtering here + guarding at render is defense-in-depth.
      const validTickers = results
        .filter(r => r && r.data && Number.isFinite(r.data.price) && Number.isFinite(r.data.change_percent))
        .map(r => r.data);

      // Merge by symbol instead of replacing the array wholesale. A single
      // symbol's transient fetch failure used to change tickers.length for
      // that cycle, which desyncs the doubled-list translateX(-50%) scroll
      // animation (its width math assumes a stable length) and produces a
      // visible snap/break every time a poll partially fails. Keeping length
      // stable once populated removes that instability.
      setTickers((prev) => {
        const bySymbol = new Map(prev.map((t) => [t.symbol, t]));
        validTickers.forEach((t) => bySymbol.set(t.symbol, t));
        // Preserve original symbol order rather than map insertion order.
        return symbols.map((s) => bySymbol.get(s)).filter(Boolean);
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching ticker prices:", error);
      // Don't leave `loading` stuck true forever on a total first-mount
      // failure — fall through to the empty-render guard below instead of
      // rendering nothing indefinitely with no retry signal.
      setLoading(false);
    }
  };

  if (loading || tickers.length === 0) return null;

  return (
    <div 
      className="ticker-container"
      style={{
        background: 'linear-gradient(90deg, #1a1f3a 0%, #0f1419 100%)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        overflow: 'hidden',
        height: '48px'
      }}
    >
      <div className="ticker-wrapper" style={{ display: 'flex', animation: 'scroll 30s linear infinite' }}>
        {[...tickers, ...tickers].map((ticker, index) => (
          <div
            key={`${ticker.symbol}-${index}`}
            className="ticker-item"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '0 24px',
              whiteSpace: 'nowrap',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onClick={() => window.location.href = `/analysis?symbol=${ticker.symbol}`}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(102, 126, 234, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <span 
              className="font-bold" 
              style={{ 
                color: '#e8eaed',
                fontSize: '14px',
                fontFamily: 'Space Grotesk, sans-serif'
              }}
            >
              {ticker.symbol}
            </span>
            <span 
              className="font-semibold"
              style={{ color: '#9ca3af', fontSize: '14px' }}
            >
              ${Number.isFinite(ticker.price) ? ticker.price.toFixed(2) : '—'}
            </span>
            <div 
              className="flex items-center gap-1"
              style={{
                color: ticker.change_percent >= 0 ? '#10b981' : '#ef4444',
                fontSize: '13px',
                fontWeight: '600'
              }}
            >
              {ticker.change_percent >= 0 ? (
                <TrendingUp className="w-3 h-3" />
              ) : (
                <TrendingDown className="w-3 h-3" />
              )}
              {Number.isFinite(ticker.change_percent)
                ? `${ticker.change_percent >= 0 ? '+' : ''}${ticker.change_percent.toFixed(2)}%`
                : '—'}
            </div>
          </div>
        ))}
      </div>
      
      <style>{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
      `}</style>
    </div>
  );
};

export default LiveTicker;